import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, TrendingUp, TrendingDown, Users, BookOpen, Target,
  Calendar, BarChart2, AlertCircle, Minus, Loader2, Filter,
  RefreshCw, Award, ChevronDown, Mail, Flame, Check
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import api from '../../api';

const pctColor = p =>
  p >= 75 ? '#10b981' : p >= 60 ? '#3b82f6' : p >= 45 ? '#f59e0b' : '#ef4444';

const trendIcon = t =>
  t === 'up' || t === 'improving' ? <TrendingUp className="w-4 h-4 text-emerald-500" />
  : t === 'down' || t === 'declining' ? <TrendingDown className="w-4 h-4 text-rose-500" />
  : <Minus className="w-4 h-4 text-slate-400" />;

// ── Weak Topic Chart ──────────────────────────────────────────────────────────
function WeakTopicChart({ classFilter }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = classFilter
      ? `/growth/admin/weak-topics?class_name=${encodeURIComponent(classFilter)}&limit=10`
      : '/growth/admin/weak-topics?limit=10';
    api.get(url)
      .then(r => { if (r.success) setData(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [classFilter]);

  if (loading) return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm flex items-center justify-center h-32">
      <Loader2 className="w-6 h-6 text-[#2563eb] animate-spin" />
    </div>
  );

  if (!data.length) return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm text-center">
      <Target className="w-8 h-8 text-slate-200 mx-auto mb-2" />
      <p className="text-sm text-slate-400">No weak topic data yet</p>
    </div>
  );

  const COLORS = ['#ef4444','#f97316','#eab308','#84cc16','#22c55e','#14b8a6','#3b82f6','#8b5cf6','#ec4899','#6b7280'];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="weak-topic-chart">
      <h3 className="font-bold text-slate-800 mb-1 text-sm flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-rose-500" /> Top Weak Topics — Class Level
      </h3>
      <p className="text-xs text-slate-400 mb-4">Topics students answer incorrectly most often (quiz + exam prep combined)</p>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
          <YAxis type="category" dataKey="topic" tick={{ fontSize: 10 }} width={120} />
          <Tooltip formatter={v => [`${v} wrong answers`, 'Count']} />
          <Bar dataKey="count" name="Wrong Answers" radius={[0, 4, 4, 0]}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Weekly Email Button ───────────────────────────────────────────────────────
function WeeklyEmailButton() {
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await api.post('/growth/send-weekly-parent-reports');
      if (res.success) {
        setSent(true);
        toast.success(`Reports sent: ${res.data.sent} parents notified, ${res.data.skipped} skipped`);
        setTimeout(() => setSent(false), 5000);
      }
    } catch {
      toast.error('Failed to send reports');
    } finally {
      setSending(false);
    }
  };

  return (
    <button onClick={handleSend} disabled={sending}
      data-testid="send-weekly-reports-btn"
      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-60 ${
        sent
          ? 'bg-emerald-600 text-white'
          : 'bg-[#2563eb] text-white hover:bg-blue-700'
      }`}>
      {sending ? <Loader2 className="w-4 h-4 animate-spin" />
        : sent ? <Check className="w-4 h-4" />
        : <Mail className="w-4 h-4" />}
      {sending ? 'Sending…' : sent ? 'Sent!' : 'Send Weekly Reports'}
    </button>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'growth',  label: 'Student Growth' },
  { key: 'prep',    label: 'AI Exam Prep Activity' },
];

// ── Student Growth Tab ────────────────────────────────────────────────────────
function GrowthTab() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [classFilter, setClassFilter] = useState('');
  const [classes,   setClasses]   = useState([]);
  const [expanded,  setExpanded]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = classFilter ? `/growth/admin/students?class_name=${encodeURIComponent(classFilter)}` : '/growth/admin/students';
      const res = await api.get(url);
      if (res.success) {
        setData(res.data);
        const cls = [...new Set((res.data.students || []).map(s => s.class_name).filter(Boolean))].sort();
        setClasses(cls);
      }
    } catch { toast.error('Failed to load growth data'); }
    finally { setLoading(false); }
  }, [classFilter]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
    </div>
  );

  const students = data?.students || [];

  return (
    <div className="space-y-5">
      {/* Summary + Send Reports Row */}
      <div className="flex items-center justify-between gap-3">
        <div className="grid grid-cols-2 gap-3 flex-1">
          {[
            { label: 'Active Students', value: data?.summary?.total_active_students ?? 0, icon: Users, color: 'bg-blue-600' },
            { label: 'School Avg', value: `${data?.summary?.school_avg ?? 0}%`, icon: BarChart2, color: 'bg-emerald-600' },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${c.color}`}>
                <c.icon className="w-[18px] h-[18px] text-white" />
              </div>
              <div>
                <div className="text-xl font-black text-slate-800">{c.value}</div>
                <div className="text-xs text-slate-500">{c.label}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <WeeklyEmailButton />
          <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
            data-testid="growth-class-filter"
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="">All Classes</option>
            {classes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Weak Topic Chart */}
      <WeakTopicChart classFilter={classFilter} />

      {/* Students Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-sm">Student Performance Overview</h3>
          <span className="text-xs text-slate-400">{students.length} students</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Student</th>
                <th className="px-4 py-3 text-left">Class</th>
                <th className="px-4 py-3 text-center">Quiz Avg</th>
                <th className="px-4 py-3 text-center">Best</th>
                <th className="px-4 py-3 text-center">Sessions</th>
                <th className="px-4 py-3 text-center">Prep Sessions</th>
                <th className="px-4 py-3 text-left">Last Activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {students.map((s, i) => (
                <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-800">{s.student_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{s.class_name || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-bold" style={{ color: pctColor(s.avg_pct) }}>{s.avg_pct}%</span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-slate-600">{s.best_pct?.toFixed(1) ?? '—'}%</td>
                  <td className="px-4 py-3 text-center text-slate-500">{s.total}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      s.prep_sessions > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'
                    }`}>{s.prep_sessions}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{(s.last_attempt || '').slice(0, 10) || '—'}</td>
                </tr>
              ))}
              {students.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">No student activity yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── AI Exam Prep Activity Tab ─────────────────────────────────────────────────
function PrepActivityTab() {
  const [data,        setData]       = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [classFilter, setClassFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/exam-prep/admin/activity?limit=50';
      if (classFilter) url += `&class_name=${encodeURIComponent(classFilter)}`;
      if (subjectFilter) url += `&subject=${encodeURIComponent(subjectFilter)}`;
      const res = await api.get(url);
      if (res.success) setData(res.data);
    } catch { toast.error('Failed to load prep activity'); }
    finally { setLoading(false); }
  }, [classFilter, subjectFilter]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
    </div>
  );

  const sessions = data?.sessions || [];
  const summary  = data?.summary || {};

  // Chart: subject distribution
  const subjCounts = {};
  sessions.forEach(s => { subjCounts[s.subject] = (subjCounts[s.subject] || 0) + 1; });
  const chartData = Object.entries(subjCounts).map(([s, c]) => ({ subject: s, count: c })).sort((a, b) => b.count - a.count).slice(0, 8);

  const classes  = [...new Set(sessions.map(s => s.class_name).filter(Boolean))].sort();
  const subjects = [...new Set(sessions.map(s => s.subject).filter(Boolean))].sort();

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <Brain className="w-[18px] h-[18px] text-white" />
          </div>
          <div>
            <div className="text-xl font-black text-slate-800">{summary.total_sessions ?? 0}</div>
            <div className="text-xs text-slate-500">Prep Sessions</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center">
            <Award className="w-[18px] h-[18px] text-white" />
          </div>
          <div>
            <div className="text-xl font-black text-slate-800">{summary.avg_score ?? 0}%</div>
            <div className="text-xs text-slate-500">Avg Score</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-600 flex items-center justify-center">
            <BookOpen className="w-[18px] h-[18px] text-white" />
          </div>
          <div>
            <div className="text-xl font-black text-slate-800">{(summary.subjects || []).length}</div>
            <div className="text-xs text-slate-500">Subjects Practiced</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
          data-testid="prep-class-filter"
          className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">All Classes</option>
          {classes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}
          data-testid="prep-subject-filter"
          className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-4 text-sm">Sessions by Subject</h3>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={chartData} margin={{ left: -20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="subject" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="Sessions" radius={[4,4,0,0]}>
                {chartData.map((_, i) => <Cell key={i} fill="#2563eb" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Sessions Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-sm">All Prep Sessions</h3>
          <span className="text-xs text-slate-400">{sessions.length} sessions</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Student</th>
                <th className="px-4 py-3 text-left">Subject</th>
                <th className="px-4 py-3 text-left">Topics</th>
                <th className="px-4 py-3 text-center">Difficulty</th>
                <th className="px-4 py-3 text-center">Score</th>
                <th className="px-4 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sessions.map((s, i) => (
                <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-800">{s.student_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{s.subject}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs max-w-[140px] truncate">
                    {(s.topics || []).slice(0, 2).join(', ')}{(s.topics || []).length > 2 ? '…' : ''}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                      s.difficulty === 'hard' ? 'bg-rose-100 text-rose-600' :
                      s.difficulty === 'medium' ? 'bg-blue-100 text-blue-600' :
                      'bg-emerald-100 text-emerald-600'
                    }`}>{s.difficulty}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {s.score_pct != null
                      ? <span className="font-bold" style={{ color: pctColor(s.score_pct) }}>{s.score_pct}%</span>
                      : <span className="text-slate-300 text-xs">Pending</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{(s.submitted_at || s.generated_at || '').slice(0, 10)}</td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400 text-sm">No prep sessions yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function StudentAIActivityPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('growth');

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="student-ai-activity-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-md">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-black text-slate-800">Student AI Activity</h1>
          <p className="text-xs text-slate-500">Academic growth & exam prep sessions across your school</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-2xl p-1 mb-6">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            data-testid={`ai-activity-tab-${t.key}`}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === t.key ? 'bg-white shadow-sm text-[#2563eb]' : 'text-slate-500 hover:text-slate-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'growth' && <GrowthTab />}
      {activeTab === 'prep'   && <PrepActivityTab />}
    </div>
  );
}
